import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalTitle } from "@td/ui";
import TaskForm from "./TaskForm";
import { translate } from "@td/shared_utils";
import { defaultTaskFormValues } from "../constants";
import { useModals } from "../contexts/modals-context";
import { mapTaskToValues, mapValuesToFormData } from "../utils/tasks";
import { useTaskSearch } from "../hooks";
import { updateTask, deleteTask } from "../apis";
import { useCurrentUser } from "../contexts/current-user-context";
import toastr from "../../toastr";
import { useTasks } from "../contexts/tasks-context";
import { parseQueryString, applyQueryString } from "../utils/urls";
import { omit } from "lodash";
import { TaskDatesSubtitle } from "../components";

const EditTaskModal = React.memo(({ taskId }) => {
  const [task, setTask] = useState(null);
  const { findTask } = useTaskSearch();
  const { closeModal } = useModals();
  const { fetchTasks } = useTasks();
  const currentUser = useCurrentUser();

  const handleCloseModal = useCallback(() => {
    const parsedQueryString = parseQueryString();

    applyQueryString(
      omit(parsedQueryString, ["task_id"])
    );

    closeModal();
  }, [closeModal]);

  const loadTask = useCallback(async (taskId) => {
    const task = await findTask(taskId);

    if (!task || task.member.isRestrictedForAdmin) {
      if (task && task.member.isRestrictedForAdmin) {
        toastr.error(
          translate(null, "care_team_tasks.notifications", "restricted_access")
        );
      }

      handleCloseModal();
      return;
    }

    setTask(task);
  }, [findTask, setTask, handleCloseModal]);

  useEffect(() => {
    loadTask(taskId);
  }, [taskId, loadTask]);

  const handleSubmit = useCallback(async (values) => {
    const { errors } = await updateTask(
      task.globalId,
      mapValuesToFormData(values)
    );

    if (!errors.length) {
      fetchTasks();

      toastr.success(
        translate(null, "care_team_tasks.notifications", "task_updated")
      );

      handleCloseModal();
    }
  }, [task, handleCloseModal, fetchTasks]);

  const handleDelete = useCallback(async (taskId) => {
    const isConfirmed = confirm(
      translate(null, "care_team_tasks.form", "delete_confirmation")
    );

    if(!isConfirmed) return;

    const { success } = await deleteTask(taskId);
    if (success) {
      toastr.success(
        translate(null, "care_team_tasks.notifications", "task_deleted")
      );
    }

    handleCloseModal();
    fetchTasks();
  }, [handleCloseModal, fetchTasks]);

  const initialValues = useMemo(() => {
    return task
      ? mapTaskToValues(task, currentUser)
      : defaultTaskFormValues;
  }, [task]);

  if (!task) {
    return null;
  }

  return (
    <Modal
      isOpen={true}
      onClose={handleCloseModal}
      title={translate(null, "care_team_tasks.edit_task_modal", "title")}
    >
      <ModalTitle
        title={translate(null, "care_team_tasks.edit_task_modal", "title")}
        subtitle={<TaskDatesSubtitle task={task} />}
      />

      <TaskForm
        task={task}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        buttonText={translate(null, "care_team_tasks.form", "save_task")}
        deleteButtonText={translate(null, "care_team_tasks.form", "delete_task")}
        handleDelete={handleDelete}
      />
    </Modal>
  );
});

EditTaskModal.propTypes = {
  taskId: PropTypes.string.isRequired,
};

export default EditTaskModal;
